import React, { useState, useEffect, useCallback, Suspense } from "react";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NightlightIcon from "@mui/icons-material/Nightlight";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningIcon from "@mui/icons-material/Warning";
import PaymentIcon from "@mui/icons-material/Payment";
import { NavLink, Outlet, useNavigate, useLocation, matchPath } from "react-router-dom";
import getDecryptedToken from "../../api/getDecryptedToken";
import baseUrl from "../../api/baseUrl";
import ReminderModel from "../../client/components/reminder/ReminderModel";
import { Circles } from "react-loader-spinner";
import { useDispatch } from "react-redux";
import { io} from "socket.io-client";
import socketauth from "../../api/socket";
import { setNewPatients } from "../../redux/patients/patientsAction";
import {
  setMyStatusChange,
  setNewAllAppointment,
  setNewPurpose,
  setStatusChange,
} from "../../redux/appointment/appointmentAction";
import { setOnlineAppointment } from "../../redux/makanappointment/makeappoinmentAction";

const clinicName: string | undefined = getDecryptedToken("clinicName");
const userName: string | undefined = getDecryptedToken("userName");
const socket = io(`${process.env.REACT_APP_WS}`, socketauth);

function Layouts({ routes }: any) {
  const [remainingMs, setRemainingMs] = useState(0);
  const [remainingStr, setRemainingStr] = useState<any>("");
  const [expiryDate, setExpiryDate] = useState<any>("");
  const [expired, setExpired] = useState(false);
  const [pay, setPay] = useState(false);
  const [open, setopen] = useState(window.innerWidth > 768);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [logout, setlogout] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
    localStorage.setItem("theme", newTheme);
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark";
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.setAttribute("data-theme", savedTheme);
      document.documentElement.classList.toggle("dark", savedTheme === "dark");
    }
  }, []);

  const handleSocketUpdate = useCallback(
    (event: any, action: Function) => {
      if (event.token.clinicId === getDecryptedToken("cid")) {
        event.data.new = true;
        dispatch(action(event.data));
      }
    },
    [dispatch]
  );

  const eid = getDecryptedToken("eid");

  useEffect(() => {
    socket.on("updatePatients", (e) => handleSocketUpdate(e, setNewPatients));
    socket.on("allAppointmentStatusChange", (e) =>
      handleSocketUpdate(e, setStatusChange)
    );
    socket.on("myAppointmentStatusChange", (e) => {
      if (eid === e.data.doctorId) {
        handleSocketUpdate(e, setMyStatusChange);
      }
    });
    socket.on("uppdateAppointment", (e) => {
      handleSocketUpdate(e, setNewAllAppointment);
    });
    socket.on("uppdateMyAppointment", (e) => {});
    socket.on("updatepurpose", (e) => handleSocketUpdate(e, setNewPurpose));

    return () => {
      socket.off("updatePatients");
      socket.off("uppdateAppointment");
      socket.off("uppdateMyAppointment");
      socket.off("updatepurpose");
    };
  }, [handleSocketUpdate, eid]);

  useEffect(() => {
    // Initialize Socket.IO connection for online appointments
    // Note: path must match the server configuration exactly
    const serverUrl = process.env.REACT_APP_WS1 || "http://192.168.8.119:5002";
    
    const socketOptions = {
      path: "/online-appointments",
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    };
    
    const onlineAppointmentsSocket = io(serverUrl, socketOptions);
    
    // Connection event handlers
    onlineAppointmentsSocket.on("connect", () => {
    });

    onlineAppointmentsSocket.on("disconnect", (reason) => {
    });

    onlineAppointmentsSocket.on("connect_error", (error: any) => {
      console.error("Online appointments socket connection error:", error);
    
    });
    
    onlineAppointmentsSocket.on("onlineAppointments", (appointment) => {
      dispatch(setOnlineAppointment(appointment));
    });
    
    // Cleanup on component unmount
    return () => {
      if (onlineAppointmentsSocket.connected) {
        onlineAppointmentsSocket.disconnect();
      }
    };
  }, [dispatch]);

  const handleClose = useCallback(() => {
    if (window.innerWidth <= 768) {
      setopen(false);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setopen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update document title based on active route (handles both NEWROUTES and ALLROUTES)
  useEffect(() => {
    if (!routes || routes.length === 0) return;

    // Helper to capitalize title properly - Title Case
    const capitalizeTitle = (title: string): string => {
      if (!title) return title;
      // Convert to Title Case: first letter of each word capitalized, rest lowercase
      return title
        .split(" ")
        .map((word) => {
          if (!word) return word;
          // Handle words with special characters
          return word
            .split(/([^a-zA-Z])/)
            .map((part) => {
              if (!part || !/^[a-zA-Z]+$/.test(part)) return part;
              return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
            })
            .join("");
        })
        .join(" ");
    };

    // Recursively find the active route and collect title hierarchy
    const findActiveRouteWithHierarchy = (routeList: any[], currentPath: string, parentPath: string = "", titleHierarchy: any[] = []): { route: any; hierarchy: any[] } | null => {
      // Sort by path length (longest first) to match more specific routes first
      const sortedRoutes = [...routeList]
        .filter((route) => {
          const path = route.PATH || route.path;
          return path && !route.DISABLED && !route.disabled;
        })
        .sort((a, b) => {
          const pathA = a.PATH || a.path || "";
          const pathB = b.PATH || b.path || "";
          return pathB.length - pathA.length;
        });

      for (const route of sortedRoutes) {
        const routePath = route.PATH || route.path;
        if (!routePath) continue;

        // Build full path for matching (handle both relative and absolute paths)
        let fullPath = routePath;
        if (!routePath.startsWith("/") && parentPath) {
          // Relative path - combine with parent
          const cleanParent = parentPath.replace(/\/+$/, "");
          const cleanRoute = routePath.replace(/^\/+/, "");
          fullPath = `${cleanParent}/${cleanRoute}`;
        } else if (!routePath.startsWith("/")) {
          // Relative path without parent - might need base path
          // Check if currentPath starts with /client
          if (currentPath.startsWith("/client")) {
            fullPath = `/client/${routePath}`;
          } else {
            fullPath = `/${routePath}`;
          }
        }

        // Use React Router's matchPath for proper route matching
        const match = matchPath(
          {
            path: fullPath,
            end: false, // Allow partial matches for nested routes
          },
          currentPath
        );

        if (match !== null) {
          const currentHierarchy = [...titleHierarchy, route];
          
          // Check if there's a more specific match in subnav/SUBNAV
          const subnav = route.SUBNAV || route.subnav;
          if (subnav && subnav.length > 0) {
            const subResult = findActiveRouteWithHierarchy(subnav, currentPath, fullPath, currentHierarchy);
            if (subResult) {
              return subResult;
            }
          }
          return { route, hierarchy: currentHierarchy };
        }

        // Also check subnav/SUBNAV recursively
        const subnav = route.SUBNAV || route.subnav;
        if (subnav && subnav.length > 0) {
          const subResult = findActiveRouteWithHierarchy(subnav, currentPath, fullPath, titleHierarchy);
          if (subResult) {
            return subResult;
          }
        }
      }
      return null;
    };

    // Find active route with its hierarchy
    const routeResult = findActiveRouteWithHierarchy(routes, location.pathname);
    
    // Capitalize clinic name
    const formattedClinicName = clinicName ? capitalizeTitle(clinicName) : null;
    
    if (routeResult && routeResult.hierarchy.length > 0) {
      // Format: clinicName | TITLE - SUBNAV.TITLE - SUBNAV.TITLE
      const routeTitles = routeResult.hierarchy
        .map(route => {
          const title = route.TITLE || route.title;
          return title ? capitalizeTitle(title) : null;
        })
        .filter(Boolean)
        .join(" - ");
      
      document.title = formattedClinicName 
        ? `${formattedClinicName} | ${routeTitles}`
        : routeTitles;
    } else if (routeResult && routeResult.route) {
      // Fallback: just use the active route title
      const title = routeResult.route.TITLE || routeResult.route.title;
      if (title) {
        const formattedTitle = capitalizeTitle(title);
        document.title = formattedClinicName 
          ? `${formattedClinicName} | ${formattedTitle}`
          : formattedTitle;
      } else {
        document.title = formattedClinicName || "Dashboard";
      }
    } else {
      // If no route found, try to match by checking if pathname starts with known base paths
      const pathSegments = location.pathname.split("/").filter(Boolean);
      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1];
        const routeBySegment = routes.find((route: any) => {
          const path = route.PATH || route.path || "";
          const normalizedPath = path.replace(/^\/+|\/+$/g, "").toLowerCase();
          return normalizedPath === lastSegment.toLowerCase() || 
                 normalizedPath.includes(lastSegment.toLowerCase());
        });
        
        if (routeBySegment) {
          const title = routeBySegment.TITLE || routeBySegment.title;
          if (title) {
            const formattedTitle = capitalizeTitle(title);
            document.title = formattedClinicName 
              ? `${formattedClinicName} | ${formattedTitle}`
              : formattedTitle;
          } else {
            document.title = formattedClinicName || "Dashboard";
          }
        } else {
          document.title = formattedClinicName || "Dashboard";
        }
      } else {
        document.title = formattedClinicName || "Dashboard";
      }
    }
  }, [location.pathname, routes, clinicName]);

  const getRemaining = (targetDate: Date) => {
    const diff = targetDate.getTime() - Date.now();
    return diff > 0 ? diff : 0;
  };

  const updateRemainingStr = (ms: number) => {
    let secs = Math.floor(ms / 1000);
    const days = Math.floor(secs / 86_400);
    secs %= 86_400;
    const h = String(Math.floor(secs / 3600)).padStart(2, "0");
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    setRemainingStr(`${days}d ${h}:${m}:${s}`);
  };

  const getData = useCallback(async () => {
    try {
      const response = await baseUrl.get(
        "client/payment/getCientClinicPaymentbyId"
      );
      if (response?.data?.success) {
        const isoString = response.data.data;
        const target = new Date(isoString);
        setExpiryDate(target);
        const remaining = getRemaining(target);
        setRemainingMs(remaining);
        setPay(remaining === 0);
        updateRemainingStr(remaining);
      } else {
        setPay(false);
      }
    } catch (error: any) {
      console.error(error?.response || error.message);
    }
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (!expiryDate) return;
    const id = setInterval(() => {
      const ms = getRemaining(expiryDate);
      setRemainingMs(ms);
      setExpired(ms === 0);
      updateRemainingStr(ms);
    }, 1000);
    return () => clearInterval(id);
  }, [expiryDate]);

  const closePaymentModel = useCallback(() => {
    setPay(false);
    getData();
  }, [getData]);

  const logOut = () => {
    localStorage.clear();
    window.location.reload();
    window.location.href = "/login";
  };

  return (
    <div className={`h-screen overflow-hidden ${theme === "dark" ? "bg-slate-900" : "bg-slate-100"}`}>
      {/* Sidebar - Fixed */}
      <aside
        className={`fixed top-0 left-0 h-full z-50 shadow-xl ${
          theme === "dark" ? "bg-slate-800" : "bg-white"
        } ${
          open 
            ? "w-[220px] translate-x-0" 
            : "md:w-[56px] md:translate-x-0 w-[220px] -translate-x-full"
        }`}
        style={{
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'width, transform'
        }}
      >
        {/* Logo Section */}
        <div 
          className={`flex items-center justify-between border-b ${theme === "dark" ? "border-slate-700 bg-slate-800" : "border-slate-100 bg-white"} ${open ? "h-20 px-4" : "md:h-16 md:justify-center md:px-2 h-20 px-4"}`}
          style={{
            transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <div 
            className="relative overflow-hidden"
            style={{
              width: open ? '200px' : '30px',
              height: open ? '60px' : '60px',
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <img 
              src={'/logo.png'} 
              alt="Care32" 
              className={`absolute inset-0 object-cover cursor-pointer transition-opacity duration-300 ${
                open ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                width: '200px',
                height: '60px'
              }}
              onClick={() => navigate("/client/dashboard")}
            />
            <img 
              src={'/logosmall.png'} 
              alt="Care32" 
              className={`absolute inset-0 object-cover cursor-pointer transition-opacity duration-300 ${
                open ? 'opacity-0' : 'opacity-100'
              }`}
              style={{
                width: '30px',
                height: '30px',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
              onClick={() => navigate("/client/dashboard")}
            />
          </div>
          <button
            onClick={() => setopen(false)}
            className="p-1.5 rounded-lg hover:bg-[#174d7a]/10 text-slate-400 hover:text-[#174d7a] active:bg-[#174d7a]/20 focus:outline-none transition-colors md:hidden flex-shrink-0"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="h-[calc(100%-64px)] overflow-y-auto py-4">
          <div 
            className={`px-3 ${!open ? "md:px-2" : ""}`}
            style={{
              transition: 'padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <ul className="space-y-1">
              {routes?.map((item: any, i: number) => (
                <li key={i}>
                  <NavLink
                    to={item?.PATH}
                    onClick={handleClose}
                    className={({ isActive }) =>
                      `flex items-center py-2 rounded-xl cursor-pointer group focus:outline-none ${
                        open ? '' : 'md:justify-center'
                      } ${
                        isActive
                          ? "text-white shadow-lg shadow-[#174d7a]/30 [&_.icon-wrapper]:bg-white/20 [&_.icon-wrapper_svg_path]:fill-white"
                          : theme === "dark"
                          ? "text-slate-300 hover:bg-slate-700 hover:text-[#174d7a] [&_.icon-wrapper]:bg-slate-700 [&_.icon-wrapper]:group-hover:bg-slate-600 [&_.icon-wrapper_svg_path]:fill-[#174d7a]"
                          : "text-slate-600 hover:bg-[#174d7a]/10 hover:text-[#174d7a] [&_.icon-wrapper]:bg-slate-100 [&_.icon-wrapper]:group-hover:bg-[#174d7a]/10 [&_.icon-wrapper_svg_path]:fill-[#174d7a]"
                      }`}
                    style={({ isActive }) => ({
                      background: isActive ? 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)' : 'transparent',
                      gap: open ? '12px' : '0px',
                      paddingLeft: open ? '12px' : '8px',
                      paddingRight: open ? '12px' : '8px',
                      justifyContent: open ? 'flex-start' : 'center',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      willChange: 'gap, padding-left, padding-right, justify-content'
                    })}
                    onMouseEnter={(e) => {
                      const navLink = e.currentTarget;
                      if (navLink.classList.contains('active') || navLink.getAttribute('aria-current') === 'page') {
                        navLink.style.background = 'linear-gradient(90deg, #1a5f95 0%, #2169a3 100%)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const navLink = e.currentTarget;
                      if (navLink.classList.contains('active') || navLink.getAttribute('aria-current') === 'page') {
                        navLink.style.background = 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)';
                      }
                    }}
                    onMouseDown={(e) => {
                      const navLink = e.currentTarget;
                      if (navLink.classList.contains('active') || navLink.getAttribute('aria-current') === 'page') {
                        navLink.style.background = 'linear-gradient(90deg, #144366 0%, #1a4d73 100%)';
                      }
                    }}
                    onMouseUp={(e) => {
                      const navLink = e.currentTarget;
                      if (navLink.classList.contains('active') || navLink.getAttribute('aria-current') === 'page') {
                        navLink.style.background = 'linear-gradient(90deg, #1a5f95 0%, #2169a3 100%)';
                      }
                    }}
                    title={!open ? item?.TITLE : undefined}
                  >
                    <div className="icon-wrapper w-6 h-6 rounded-lg flex items-center justify-center transition-colors [&_svg]:w-4 [&_svg]:h-4 flex-shrink-0">
                      {item?.ICON && <item.ICON />}
                    </div>
                    <span 
                      className={`text-xs font-medium capitalize whitespace-nowrap overflow-hidden inline-block ${
                        open ? 'opacity-100' : 'md:opacity-0 opacity-100'
                      }`}
                      style={{
                        maxWidth: open ? '200px' : '0px',
                        width: open ? 'auto' : '0px',
                        transition: 'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1) 0.05s, max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        willChange: 'opacity, max-width, width'
                      }}
                    >
                      {item?.TITLE}
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </aside>

      {/* Mobile Overlay */}
      <div
        onClick={() => setopen(false)}
        className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Main Content Wrapper */}
      <div
        className={`h-full flex flex-col ${
          open ? "md:ml-[220px]" : "md:ml-[56px] ml-0"
        }`}
        style={{
          transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left'
        }}
      >
        {/* Header - Fixed */}
        <header className={`h-14 border-b flex items-center justify-between px-4 md:px-6 shadow-sm flex-shrink-0 ${theme === "dark" ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
          {/* Left Section */}
          <div className="flex items-center gap-4 h-full">
          <button
            onClick={() => setopen((e) => !e)}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-white shadow-md hover:shadow-lg hover:scale-105 focus:outline-none transition-all"
            style={{
              background: 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(90deg, #1a5f95 0%, #2169a3 100%)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.background = 'linear-gradient(90deg, #144366 0%, #1a4d73 100%)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.background = 'linear-gradient(90deg, #1a5f95 0%, #2169a3 100%)';
            }}
            aria-label="Toggle menu"
          >
              {open ? (
                <MenuOpenIcon sx={{ fontSize: 18 }} />
              ) : (
                <MenuIcon sx={{ fontSize: 18 }} />
              )}
            </button>
            <h1 className={`hidden sm:block text-xl font-bold uppercase leading-none mb-0 ${theme === "dark" ? "text-white" : "text-slate-800"}`}>
              {clinicName}
            </h1>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Subscription Status - Desktop */}
            <div className={`hidden lg:flex items-center gap-3 px-2 py-2 rounded-xl border ${theme === "dark" ? "bg-slate-700 border-slate-600" : "bg-slate-50 border-slate-200"}`}>
              <div className="flex items-center gap-2">
                <AccessTimeIcon
                  sx={{ fontSize: 18 }}
                  className={expired ? "text-red-500" : "text-emerald-500"}
                />
                {expired ? (
                  <div className="flex items-center gap-1 text-red-500">
                    <WarningIcon sx={{ fontSize: 16 }} />
                    <span className="text-sm font-semibold">Expired</span>
                  </div>
                ) : (
                  <span className={`text-sm font-mono font-semibold ${theme === "dark" ? "text-slate-200" : "text-slate-700"}`}>
                    {remainingStr}
                  </span>
                )}
              </div>
              <button
                onClick={() => navigate("/client/pay")}
                className="flex items-center gap-1 px-3 py-1.5 text-white text-xs font-semibold rounded-lg focus:outline-none hover:shadow-md transition-all"
                style={{
                  background: 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(90deg, #1a5f95 0%, #2169a3 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)';
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(90deg, #144366 0%, #1a4d73 100%)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(90deg, #1a5f95 0%, #2169a3 100%)';
                }}
              >
                <PaymentIcon sx={{ fontSize: 14 }} />
                Pay
              </button>
            </div>

            {/* Mobile Pay Button */}
            <button
              onClick={() => navigate("/client/pay")}
              className="lg:hidden p-2 rounded-xl text-white focus:outline-none hover:shadow-md transition-all"
              style={{
                background: 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #1a5f95 0%, #2169a3 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)';
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #144366 0%, #1a4d73 100%)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #1a5f95 0%, #2169a3 100%)';
              }}
            >
              <PaymentIcon sx={{ fontSize: 20 }} />
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-xl transition-colors focus:outline-none ${theme === "dark" ? "hover:bg-slate-700 hover:text-[#174d7a] text-slate-300 active:bg-slate-600" : "hover:bg-[#174d7a]/10 hover:text-[#174d7a] text-slate-600 active:bg-[#174d7a]/20"}`}
              aria-label="Toggle theme"
            >
              {theme === "light" ? (
                <NightlightIcon sx={{ fontSize: 22 }} />
              ) : (
                <LightModeIcon sx={{ fontSize: 22 }} className="text-amber-500" />
              )}
            </button>

            {/* Notifications */}
            <button className={`p-2 rounded-xl transition-colors relative focus:outline-none ${theme === "dark" ? "hover:bg-slate-700 hover:text-[#174d7a] text-slate-300 active:bg-slate-600" : "hover:bg-[#174d7a]/10 hover:text-[#174d7a] text-slate-600 active:bg-[#174d7a]/20"}`}>
              <NotificationsIcon sx={{ fontSize: 22 }} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setlogout(!logout)}
                className={`flex items-center gap-2 p-1.5 pr-3 rounded-xl transition-colors focus:outline-none ${theme === "dark" ? "hover:bg-slate-700 active:bg-slate-600" : "hover:bg-[#174d7a]/10 active:bg-[#174d7a]/20"}`}
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-md"
                  style={{
                    background: 'linear-gradient(90deg, #174d7a 0%, #1d5c91 100%)'
                  }}
                >
                  {userName?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <span className={`hidden md:block text-sm font-medium max-w-[100px] truncate capitalize ${theme === "dark" ? "text-slate-200" : "text-slate-700"}`}>
                  {userName}
                </span>
              </button>

              {/* Dropdown */}
              {logout && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setlogout(false)}
                  />
                  <div className={`absolute right-0 top-full mt-2 w-48 rounded-xl shadow-xl border py-2 z-50 ${theme === "dark" ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                    <div className={`px-4 py-2 border-b ${theme === "dark" ? "border-slate-700" : "border-slate-100"}`}>
                      <p className={`text-sm font-semibold truncate ${theme === "dark" ? "text-white" : "text-slate-800"}`}>
                        {userName}
                      </p>
                      <p className={`text-xs truncate ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                        {clinicName}
                      </p>
                    </div>
                    <button
                      onClick={logOut}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 active:bg-red-100 focus:outline-none transition-colors"
                    >
                      <LogoutIcon sx={{ fontSize: 18 }} />
                      Log Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Main Content Area - Scrollable */}
        <main className={`flex-1 overflow-y-auto pt-1.5 pb-1.5 px-1.5 md:pt-2 md:pb-2 md:px-2 ${theme === "dark" ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30"}`}>
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full">
                <Circles
                  height="60"
                  width="60"
                  color="#174d7a"
                  ariaLabel="loading"
                />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Payment Reminder Modal */}
      {pay && <ReminderModel closeModel={closePaymentModel} theme={theme} />}
    </div>
  );
}

export default Layouts;
